import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import type { SportTypes } from '@racemap/sdk/schema/events';
import {
  DBSortOrder,
  type EventCounts,
  type EventsQuery,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Button, Empty, Spin } from 'antd';
import { type ComponentProps, type FC, useEffect, useRef, useState } from 'react';
import { LiveIndicator } from 'src/components/LiveIndicator';
import { useIsMobile } from 'src/lib/customHooks';
import { useDebounceValue } from 'src/lib/customHooks/useDebounce';
import { useEventsPaginated } from 'src/lib/customHooks/useEventsPaginated';
import { useURLStates } from 'src/lib/customHooks/useURLStates';
import { trackEvent } from 'src/lib/googleAnalytics';
import useSWR from 'swr';
import { EventCard } from './EventCard';
import { EventsMap, STATUS_COLORS } from './EventsMap';
import { EventsToolbar, type StatusFilter } from './EventsToolbar';
import { WATER_COLOR } from './eventsMapStyle';
import { activeSection, type EventStatus, statusOf } from './eventsOverview';

// The player's own pattern: VITE_CDN_HOST is the edge host in production and the dev proxy locally,
// and the anonymous client is what lets Cloudflare share one cached answer.
const CDN_HOST = import.meta.env.VITE_CDN_HOST;
const apiClient = new RacemapAPIClient(CDN_HOST, { includeCredentials: false });

const PAGE_SIZE = 24;

const URL_STATES = {
  q: { defaultValue: '' as string, paramType: 'string' as const },
  status: { defaultValue: 'all' as string, paramType: 'string' as const },
  sport: { defaultValue: [] as Array<string>, isMultiple: true, paramType: 'string' as const },
};

const SECTIONS: Array<{ key: EventStatus; title: string }> = [
  { key: 'live', title: 'Live now' },
  { key: 'upcoming', title: 'Upcoming' },
  { key: 'past', title: 'Past' },
];

const globalStyles = {
  body: {
    margin: 0,
    fontFamily:
      '-apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
    fontSize: 14,
    color: '#212529',
    background: WATER_COLOR,
  },
  a: { color: '#36739a', textDecoration: 'none' },
};

const Page = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 30px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding-bottom: 7px;
  border-bottom: 1px solid #dee2e6;
  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #202020;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 15px;
  padding: 0 20px;
  font-size: 12px;
  color: #646464;
  span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

const fmt = (n: number | undefined) => (n ?? 0).toLocaleString('en-US').replace(/,/g, ' ');

type EventList = ReturnType<typeof useEventsPaginated>;
type EventFilters = Pick<EventsQuery, 'search' | 'findBySportTypes'>;

const isExhausted = (shown: Set<EventStatus>, key: EventStatus, list: EventList) =>
  !shown.has(key) || (list.firstPageLoaded && !list.hasNextPage);

/** URL filters, the debounced search, and the counts and map points they select. */
function useEventFilters(showMap: boolean, bbox: string | undefined) {
  const [url, setUrl] = useURLStates(URL_STATES);
  const status = url.status as StatusFilter;
  const sports = url.sport as Array<SportTypes>;
  const [search] = useDebounceValue(url.q, 300);
  // One event per search, not per typing pause.
  const [trackedSearch] = useDebounceValue(url.q, 1500);

  // Length only: the search term itself may be personal.
  useEffect(() => {
    if (trackedSearch) trackEvent('event_list_search', { query_len: trackedSearch.length });
  }, [trackedSearch]);

  const filters: EventFilters = {
    search: search || undefined,
    findBySportTypes: sports.length ? sports : undefined,
  };
  const { data: counts } = useSWR(['EVENT_COUNTS', search, sports.join(','), bbox], () =>
    apiClient.getEventCounts({ ...filters, bbox }),
  );
  const { data: mapPoints = [] } = useSWR(
    showMap ? ['EVENT_MAP', search, sports.join(',')] : null,
    () => apiClient.getEventsMap(filters),
  );
  // Same key as the unfiltered counts above, so an unfiltered load fetches once.
  const { data: totals } = useSWR(['EVENT_COUNTS', '', '', undefined], () =>
    apiClient.getEventCounts(),
  );

  return { url, setUrl, status, sports, filters, counts, mapPoints, totals };
}

/** The visible sections, loaded one after another: the next starts once the previous is exhausted. */
function useEventSections(status: StatusFilter, filters: EventFilters, bbox: string | undefined) {
  const visibleSections = SECTIONS.filter((s) => status === 'all' || s.key === status);
  const shown = new Set(visibleSections.map((s) => s.key));

  const common = {
    searchValue: filters.search,
    sportTypes: filters.findBySportTypes,
    show: ['parentsonly' as const],
    bbox,
    pageSize: PAGE_SIZE,
    apiClient,
  };
  const live = useEventsPaginated({
    ...common,
    filter: 'running',
    sort: DBSortOrder.Ascending,
    fetchDisabled: !shown.has('live'),
  });
  const liveDone = isExhausted(shown, 'live', live);
  const upcoming = useEventsPaginated({
    ...common,
    filter: 'future',
    sort: DBSortOrder.Ascending,
    fetchDisabled: !shown.has('upcoming') || !liveDone,
  });
  const upcomingDone = isExhausted(shown, 'upcoming', upcoming);
  const past = useEventsPaginated({
    ...common,
    filter: 'past',
    sort: DBSortOrder.Descending,
    fetchDisabled: !shown.has('past') || !upcomingDone,
  });

  const lists = { live, upcoming, past };
  const active = activeSection([
    { key: 'live' as const, exhausted: liveDone },
    { key: 'upcoming' as const, exhausted: upcomingDone },
    { key: 'past' as const, exhausted: isExhausted(shown, 'past', past) },
  ]);
  return { visibleSections, lists, active };
}

/** Infinite scroll: while the sentinel is in view, keep loading the active section. */
function useInfiniteScroll(lists: Record<EventStatus, EventList>, active: EventStatus | null) {
  const activeList = active != null ? lists[active] : null;
  const sentinel = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(false);
  // Not scrollY: it stays 0 when an embedding page sizes the iframe and scrolls itself.
  const leftView = useRef(false);
  useEffect(() => {
    const el = sentinel.current;
    if (el == null) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) leftView.current = true;
        setAtBottom(entry.isIntersecting);
      },
      { rootMargin: '600px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (
      atBottom &&
      activeList?.firstPageLoaded &&
      activeList.hasNextPage &&
      !activeList.isValidating
    ) {
      activeList.loadNextPage();
      // Only pages the user scrolled for, not the ones chained in on load.
      if (active != null && leftView.current) {
        trackEvent('event_list_load_more', { section: active });
      }
    }
  }, [atBottom, activeList, active]);
  const loading = activeList != null && (activeList.isLoading || activeList.isValidating);
  return { sentinel, loading };
}

const PageHeader: FC<{ totals: EventCounts | undefined }> = ({ totals }) => (
  <header>
    <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, color: '#202020' }}>Events</h1>
    <p style={{ margin: '5px 0 0', color: '#646464', fontSize: 15 }}>
      Follow an event live on the map, or watch the replay afterwards.
    </p>
    {totals && (
      <p style={{ margin: '5px 0 0', color: '#404040', fontSize: 14 }}>
        <strong>{fmt(totals.live)}</strong> live now · <strong>{fmt(totals.upcoming)}</strong>{' '}
        upcoming · <strong>{fmt(totals.live + totals.upcoming + totals.past)}</strong> events
        tracked so far
      </p>
    )}
  </header>
);

const MapSection: FC<ComponentProps<typeof EventsMap>> = ({ areaActive, ...mapProps }) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <EventsMap {...mapProps} areaActive={areaActive} />
    <Legend>
      {SECTIONS.map(({ key, title }) => (
        <span key={key}>
          <Dot style={{ background: STATUS_COLORS[key] }} />
          {title}
        </span>
      ))}
      <span>
        {areaActive
          ? 'The list shows the map area.'
          : 'Move or zoom the map to filter the list. Events without a location are not on the map.'}
      </span>
    </Legend>
  </section>
);

type EventSectionProps = {
  status: EventStatus;
  title: string;
  events: Array<RacemapEvent>;
  count: number | undefined;
  hoveredId: string | null;
  onHover: (id: string | null) => void;
};

const EventSection: FC<EventSectionProps> = ({
  status,
  title,
  events,
  count,
  hoveredId,
  onHover,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
    <SectionHead>
      {status === 'live' && <LiveIndicator />}
      <h2>{title}</h2>
      <span style={{ color: '#646464' }}>{count != null ? fmt(count) : ''}</span>
    </SectionHead>
    <Grid>
      {events.map((event) => (
        <EventCard
          key={event.id}
          event={event}
          highlighted={hoveredId === event.id}
          onHover={onHover}
        />
      ))}
    </Grid>
  </section>
);

export const EventsOverviewPage: FC = () => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mapBbox, setBbox] = useState<string | undefined>();
  // ponytail: no map on phones for now, so no area filter either.
  const showMap = !useIsMobile();
  const bbox = showMap ? mapBbox : undefined;
  const [mapResetToken, setMapResetToken] = useState(0);

  const { url, setUrl, status, sports, filters, counts, mapPoints, totals } = useEventFilters(
    showMap,
    bbox,
  );
  const { visibleSections, lists, active } = useEventSections(status, filters, bbox);
  const { sentinel, loading } = useInfiniteScroll(lists, active);

  const isEmpty = active == null && visibleSections.every((s) => lists[s.key].data.length === 0);
  const visiblePoints = mapPoints.filter((p) => status === 'all' || statusOf(p) === status);
  const resetArea = () => {
    setBbox(undefined);
    setMapResetToken((t) => t + 1);
  };
  const hasFilters = Boolean(url.q || sports.length || status !== 'all' || bbox);
  const clearAll = () => {
    trackEvent('event_list_filter', { filter: 'clear', value: '' });
    setUrl({ q: '', sport: [], status: 'all' });
    resetArea();
  };

  return (
    <Page>
      <Global styles={globalStyles} />
      <PageHeader totals={totals} />

      {showMap && (
        <MapSection
          points={visiblePoints}
          hoveredId={hoveredId}
          onAreaChange={setBbox}
          resetToken={mapResetToken}
          areaActive={bbox != null}
        />
      )}

      <EventsToolbar
        query={url.q}
        onQueryChange={(q) => setUrl({ q })}
        status={status}
        onStatusChange={(s) => {
          trackEvent('event_list_filter', { filter: 'status', value: s });
          setUrl({ status: s });
        }}
        sports={sports}
        onSportsChange={(sport) => {
          trackEvent('event_list_filter', { filter: 'sport', value: [...sport].sort().join(',') });
          setUrl({ sport });
        }}
        counts={counts}
        areaActive={bbox != null}
        onResetArea={() => {
          trackEvent('event_list_filter', { filter: 'map_area', value: '' });
          resetArea();
        }}
        hasFilters={hasFilters}
        onClearAll={clearAll}
      />

      {isEmpty && (
        <Empty description="No events match these filters.">
          <Button onClick={clearAll}>Clear filters</Button>
        </Empty>
      )}

      {visibleSections.map(({ key, title }) =>
        lists[key].data.length === 0 ? null : (
          <EventSection
            key={key}
            status={key}
            title={title}
            events={lists[key].data}
            count={counts?.[key]}
            hoveredId={hoveredId}
            onHover={setHoveredId}
          />
        ),
      )}

      <div ref={sentinel} style={{ display: 'flex', justifyContent: 'center', minHeight: 40 }}>
        {loading && <Spin />}
      </div>
    </Page>
  );
};
